// Mock data for PROCTO 13 LLC website

export const siteConfig = {
  companyName: "PROCTO 13 LLC",
  tagline: "Brand-safe retail execution across marketplaces and direct channels.",
  email: "sales@prosto13.com",
  phone: "+1 (646) 980-3291",
  address: "1420 Gemini Blvd. Unit 12, Orlando, FL 32837 USA",
};

export const navigation = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Partner With Us", href: "/partner" },
  { name: "Why It's Safe", href: "/why-safe" },
  { name: "Team", href: "/team" },
  { name: "Careers", href: "/careers" },
  { name: "Contact", href: "/contact" },
];

export const heroData = {
  headline: "We buy, own, and resell your products online — with MAP discipline and brand control.",
  subheadline: "PROCTO 13 LLC is an inventory-backed retail partner. We purchase through clean POs, then sell across marketplaces and niche online stores — focusing on long-tail SKUs, approved merchandising, and predictable reorders.",
  proofBullets: [
    "Unlock demand for ignored SKUs (long-tail / low-liquidity)",
    "Protect pricing & brand equity (MAP-aware discipline)",
    "Approved content improvements (you stay in control)",
    "Clean POs + repeat reorders (stable operations)",
  ],
  primaryCTA: { text: "Partner With Us", href: "/partner" },
  secondaryCTA: { text: "Contact Sales", href: "/contact" },
};

// We Are / We Are Not section
export const identityBlock = {
  weAre: [
    "Inventory-owning reseller (we take stock risk)",
    "Brand-safe operator (MAP-aware, compliant)",
    "Multi-channel seller (marketplaces + niche sites)",
  ],
  weAreNot: [
    "An agency or consultant",
    "A dropshipper",
    'A "race-to-the-bottom" marketplace seller',
    "A sub-distributor (no redistribution)",
  ],
};

// How It Works - 3 steps
export const howItWorksSimple = [
  {
    step: 1,
    title: "You sell to us (clean PO)",
    description: "We buy inventory and handle compliance/documents.",
  },
  {
    step: 2,
    title: "We execute online",
    description: "Listings, ads, merchandising with MAP discipline and approvals.",
  },
  {
    step: 3,
    title: "We reorder based on performance",
    description: "Predictable, repeatable purchasing.",
  },
];

// Fit / Not a Fit
export const fitBlock = {
  goodFit: [
    "You want incremental revenue without channel conflict",
    "You care about MAP / pricing integrity",
    "You have underperforming or long-tail SKUs",
  ],
  notFit: [
    "You want aggressive discounting",
    "You want redistribution to other sellers",
    "You want zero control over content/pricing",
  ],
};

// Proof cards
export const proofCards = [
  { label: "Inventory-backed", value: "We own stock & take risk" },
  { label: "Ops capacity", value: "~300 units/day processed" },
  { label: "Response time", value: "3–4 hour support SLA" },
  { label: "No parallel imports", value: "Full supply chain control" },
];

export const whyPartner = [
  {
    title: "Unlock overlooked revenue",
    description: "We help brands capture demand in long-tail and underperforming SKUs through focused assortment selection and disciplined execution.",
  },
  {
    title: "Protect brand value",
    description: "MAP-aware pricing discipline and brand-safe positioning designed to support long-term brand equity—not short-term tactics.",
  },
  {
    title: "In-house execution",
    description: "Keyword research, SEO foundations, and PPC execution—handled internally and aligned to brand guidelines.",
  },
  {
    title: "Easy to work with",
    description: "Fast onboarding, one point of contact, clean documentation, and predictable purchasing operations.",
  },
];

export const businessModel = {
  title: "Our business model",
  description: "We operate an inventory-backed, multi-channel retail model tailored to each brand. We combine controlled marketplace presence (Amazon when appropriate) with direct channels such as an online catalog and email-driven distribution.",
};

export const complianceTeaser = {
  title: "Compliance-first approach",
  points: [
    "MAP-aware pricing discipline",
    "Approval-based listing/content updates (when applicable)",
    "Merchandising aligned to brand positioning",
    "Channel boundaries respected when required",
    "Clean documentation and predictable operations",
  ],
  linkText: "Read our Compliance Policy",
  linkHref: "/compliance",
};

export const capabilities = [
  {
    title: "Demand Intelligence",
    description: "Keyword research, demand mapping, and assortment focus.",
    icon: "TrendingUp",
  },
  {
    title: "Performance Execution",
    description: "PPC/Sponsored Ads with brand-safe scaling and disciplined decision-making.",
    icon: "BarChart3",
  },
  {
    title: "Catalog & Merchandising",
    description: "SEO foundations and listing improvements with brand approval, aligned to positioning.",
    icon: "LayoutGrid",
  },
  {
    title: "Operations & CX",
    description: "Customer communication workflows, objection handling, and low-friction purchasing operations.",
    icon: "Headphones",
  },
];

export const howWeWork = [
  { step: 1, title: "Intro & requirements" },
  { step: 2, title: "Brand alignment (channels, MAP, content guidelines)" },
  { step: 3, title: "SKU focus and launch plan" },
  { step: 4, title: "Purchase orders + execution (clean POs, merchandising)" },
  { step: 5, title: "Reorders + scaling based on performance" },
];

export const partnerPageData = {
  headline: "Partner With Us",
  intro: "We buy inventory, handle fulfillment, and scale reorders based on performance. Clean POs, fast onboarding, one point of contact.",
  expectations: [
    "Clean POs with predictable reorders",
    "MAP-aware pricing",
    "Approval-based listing improvements",
    "Fast onboarding, one contact",
  ],
  onboardingChecklist: [
    "Wholesale requirements / line sheet",
    "MAP policy (if applicable)",
    "Content guidelines / approved assets (if available)",
    "Commercial terms (MOQ/case packs/lead times)",
    "Shipping and billing setup",
  ],
};

export const faqData = [
  {
    question: "Do you sell on marketplaces such as Amazon?",
    answer: "We maintain a controlled presence on major marketplaces when appropriate for the brand, alongside direct channels such as our online catalog and email distribution. Our focus is sustainable growth and brand integrity.",
  },
  {
    question: "Do you follow MAP policies?",
    answer: "Yes. Where MAP applies, we operate with MAP-aware pricing discipline and avoid race-to-the-bottom behavior that harms long-term brand value.",
  },
  {
    question: "Will you change listings or content without approval?",
    answer: "No. We treat brand presentation seriously. Listing/content updates are aligned with brand guidelines and handled on an approval basis when required.",
  },
  {
    question: "Are you a dropshipper?",
    answer: "No. We operate an inventory-backed model and focus on clean purchasing operations and repeatable reorders.",
  },
  {
    question: "Do you redistribute products to other sellers?",
    answer: "No. We operate as an end retailer partner and do not redistribute inventory to third-party sellers.",
  },
  {
    question: "What channels do you use besides marketplaces?",
    answer: "We use an online catalog and email-driven distribution, and we tailor channel mix based on the brand's target audience and strategy.",
  },
  {
    question: "How fast is onboarding?",
    answer: "If requirements and documentation are clear, onboarding can move quickly. Our goal is a low-friction setup with minimal back-and-forth.",
  },
  {
    question: "What makes you \"easy to work with\"?",
    answer: "One point of contact, clean documentation, fast decisions, and no unnecessary negotiation cycles. We aim for operational simplicity.",
  },
  {
    question: "Can you follow channel restrictions?",
    answer: "Yes. If a brand has channel boundaries, we can align to them as part of the partnership structure.",
  },
  {
    question: "How do you handle customer communication and objections?",
    answer: "We maintain structured customer support workflows and handle common objections and inquiries with a brand-safe approach.",
  },
];

export const complianceData = {
  headline: "Compliance & Brand Integrity",
  intro: "We partner with brands that value long-term equity. Our approach is built around pricing discipline, brand-safe merchandising, and operational clarity.",
  sections: [
    {
      title: "MAP Discipline",
      description: "We operate with MAP-aware pricing discipline where applicable and avoid tactics that undermine long-term brand value.",
      icon: "Shield",
    },
    {
      title: "Brand-safe Content & Merchandising",
      description: "We aim to present brands consistently and professionally across channels, aligned with approved assets and positioning.",
      icon: "FileCheck",
    },
    {
      title: "Approval-based Listing Updates",
      description: "When listing/content changes are needed, we follow brand guidelines and operate on an approval basis when required.",
      icon: "CheckCircle",
    },
    {
      title: "Channel Boundaries",
      description: "We can follow channel restrictions and partnership terms when a brand requires them.",
      icon: "Lock",
    },
    {
      title: "Operational Simplicity",
      description: "Clean POs, clear documentation, predictable communication, and low-friction processes.",
      icon: "ClipboardCheck",
    },
  ],
};

export const teamData = [
  {
    name: "Michael Chen",
    role: "Head of Commerce Operations",
    description: "Ensures clean execution, predictable operations, and partner experience.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
  },
  {
    name: "Sarah Mitchell",
    role: "Brand & MAP Compliance Manager",
    description: "Maintains pricing discipline and brand integrity standards.",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop&crop=face",
  },
  {
    name: "David Park",
    role: "Catalog & Merchandising Lead",
    description: "Drives brand-safe merchandising and catalog quality.",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=face",
  },
  {
    name: "Emily Rodriguez",
    role: "Performance Marketing Specialist (PPC)",
    description: "Executes sponsored ads with controlled scaling.",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face",
  },
  {
    name: "James Wilson",
    role: "Search & Demand Intelligence",
    description: "Identifies demand signals and visibility opportunities.",
    image: "https://images.unsplash.com/photo-1463453091185-61582044d556?w=400&h=400&fit=crop&crop=face",
  },
  {
    name: "Lisa Thompson",
    role: "Customer Experience & Objections Lead",
    description: "Handles customer workflows and objection management.",
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop&crop=face",
  },
  {
    name: "Robert Kim",
    role: "Supply & Replenishment Coordinator",
    description: "Manages purchasing cadence and reorder operations.",
    image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop&crop=face",
  },
];

export const aboutData = {
  headline: "About PROCTO 13 LLC",
  intro: "PROCTO 13 LLC is an inventory-owning reseller. We buy products through clean POs, take stock risk, and sell online through marketplaces and niche eCommerce sites — under MAP discipline and brand guidelines.",
  whyPoints: [
    "We focus on long-tail and low-liquidity SKUs that need execution attention",
    "We operate brand-safe: MAP-aware pricing, approved content, no redistribution",
    "We're operators, not consultants — we own stock and take the risk",
  ],
  mission: "Brands shouldn't have to choose between online growth and channel control. We deliver incremental online revenue while protecting pricing and brand presentation.",
  whatWeDo: [
    "Buy inventory (clean PO)",
    "Operate listings + ads + merchandising",
    "Sell across marketplaces + direct channels",
    "Reorder based on performance",
  ],
  whatWeDont: [
    "Dropship",
    "Arbitrage from retail",
    "Break MAP",
    "Resell to sub-distributors",
  ],
};

export const contactData = {
  headline: "Start a partnership conversation",
  email: "sales@prosto13.com",
  phone: "+1 (646) 980-3291",
  checkboxOptions: [
    "Send wholesale requirements / line sheet",
    "Discuss MAP / channel rules",
    "Request onboarding checklist",
  ],
};

export const whySafeData = {
  headline: "Why It's Safe to Work With Us",
  intro: "We focus on reliable operations, quality control, and responsible pricing—so brands can grow without risk.",
  pillars: [
    {
      title: "Reliable fulfillment",
      description: "Fast, predictable shipping with quality checks on every order.",
      result: "Consistent delivery and protected brand reputation.",
      icon: "Truck",
    },
    {
      title: "Quality control",
      description: "Every shipment is inspected. We treat packaging as part of the brand experience.",
      result: "Fewer returns and complaints.",
      icon: "PackageCheck",
    },
    {
      title: "Fast support (3-4 hours)",
      description: "Our team responds quickly to customer inquiries.",
      result: "Higher satisfaction, fewer escalations.",
      icon: "Headphones",
    },
    {
      title: "Proper storage & handling",
      description: "We monitor expiration dates and storage conditions.",
      result: "Products arrive in optimal condition.",
      icon: "Thermometer",
    },
    {
      title: "Responsible pricing",
      description: "We follow MAP policies and communicate openly with brands.",
      result: "Stable pricing, healthy channel relationships.",
      icon: "Scale",
    },
    {
      title: "No parallel imports",
      description: "We do not engage in parallel imports. NDA available.",
      result: "Full supply chain control.",
      icon: "ShieldCheck",
    },
  ],
  closingLine: "A low-risk, disciplined retail partner focused on long-term value.",
};
